<template>
  <base-page-layout :title="title">
    <v-container fluid class="px-1">
      <v-skeleton-loader type="article, list-item-three-line@4" :loading="loading">
        <v-card>
          <v-card-title>
            <div class="d-flex align-center">
              <version-tag v-if="currentConfiguration" :mode="currentConfiguration.mode || 'NEW'" :version="currentConfiguration.version || 0" />
              <span v-if="versionSubtitle" class="subtitle ml-3">({{ versionSubtitle }})</span>
            </div>
            <v-spacer />
            <mf-button
              v-if="currentConfiguration && currentConfiguration.mode !== 'PREVIEW'"
              class="mr-3"
              outlined
              label="Ver versão em preview"
              @click="setCurrentConfiguration('preview')"
            />
            <mf-button
              v-if="currentConfiguration && currentConfiguration.mode !== 'PRODUCTION'"
              class="mr-3"
              outlined
              label="Ver versão em produção"
              @click="setCurrentConfiguration('production')"
            />
            <hotsite-configuration-selection @select="selectConfiguration" />
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <div style="display: flex; flex-direction: column">
                  <div style="display: flex; align-items: center; gap: 4px">
                    <span class="subtitle-font">URLs de produção</span>
                    <hotsite-urls v-if="isAdmin && hotsiteSlot" v-model="hotsiteSlot.urls" :saveFn="saveProductionUrls" />
                  </div>
                  <div v-for="url in productionUrls" :key="url">
                    <a :href="url" target="_blank">{{ url }}</a> <copy-button size="xs" tooltip="Copiar URL" @click="copyUrl(url)" />
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div style="display: flex; flex-direction: column">
                  <span class="subtitle-font">URL de preview</span>
                  <div>
                    <mf-tooltip bottom max-width="250px">
                      <template #activator="{ on, attrs }">
                        <a :href="previewUrl" target="_blank" v-bind="attrs" v-on="on">{{ previewUrl }}</a>
                      </template>
                      <span>Para visualizar as atualizações em preview, é necessário primeiro salvar as alterações como preview.</span>
                    </mf-tooltip>
                    <copy-button size="xs" tooltip="Copiar URL" @click="copyUrl(previewUrl)" />
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <mf-tabs-menu v-model="tab" :tabs="tabs" :tabs-errors="tabsErrors" class="mb-4" />

                <hotsite-configuration-colors v-show="tab === 0" />
                <hotsite-configuration-images v-show="tab === 1" />
                <hotsite-configuration-positions v-show="tab === 2" />
                <hotsite-configuration-fields v-show="tab === 3" />
                <hotsite-configuration-advanced v-show="tab === 4" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <mf-action-buttons
          class="mt-3"
          :primary-button="{
            text: 'Salvar em produção',
            action: saveProduction,
            isVisible: true,
            isLoading: savingProduction,
            isDisabled: savingPreview,
          }"
          :secondary-button="{
            text: 'Salvar em preview',
            action: savePreview,
            isVisible: true,
            isLoading: savingPreview,
            isDisabled: savingProduction,
          }"
          :cancel-button="{
            text: 'Descartar alterações',
            action: discardChanges,
            isVisible: true,
            isLoading: $apollo.loading,
            isDisabled: savingPreview || savingProduction,
          }"
        />
      </v-skeleton-loader>
    </v-container>
  </base-page-layout>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import { SAVE_HOTSITE_SLOT_PREVIEW, SAVE_HOTSITE_SLOT_PRODUCTION, SAVE_HOTSITE_PRODUCTION_URLS } from '@/modules/configuracoes/site-de-cadastramento/graphql'

export default {
  name: 'HotsiteSlot',
  components: {
    BasePageLayout: () => import('@/components/BasePageLayout.vue'),
    HotsiteConfigurationSelection: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteConfigurationSelection.vue'),
    HotsiteConfigurationColors: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteConfigurationColors.vue'),
    HotsiteConfigurationImages: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteConfigurationImages.vue'),
    HotsiteConfigurationPositions: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteConfigurationPositions.vue'),
    HotsiteConfigurationAdvanced: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteConfigurationAdvanced.vue'),
    HotsiteConfigurationFields: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteConfigurationFields.vue'),
    VersionTag: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/VersionTag.vue'),
    HotsiteUrls: () => import('@/modules/configuracoes/site-de-cadastramento/views/components/HotsiteUrls.vue'),
    CopyButton: () => import('@/components/CopyButton.vue'),
  },
  data: () => ({
    tab: 0,
    savingPreview: false,
    savingProduction: false,
  }),
  computed: {
    ...mapState('hotsiteConfiguration', ['hotsiteSlot', 'loading']),
    ...mapGetters('hotsiteConfiguration', [
      'hasData',
      'currentConfiguration',
      'currentConfigurationToSave',
      'hasValidationErrors',
      'getTabValidationErrors',
      'getFieldValidationError',
    ]),
    isAdmin() {
      return this.$getIsAdmin()
    },
    productionUrls() {
      if (!this.hotsiteSlot) return []
      return this.hotsiteSlot.urls.map((url) => {
        if (url.startsWith('https://')) {
          return url
        }
        return `https://${url}`
      })
    },
    versionSubtitle() {
      if (!this.currentConfiguration?.created_at) return ''
      const date = DateTime.fromISO(this.currentConfiguration.created_at).toLocaleString(DateTime.DATE_FULL)
      const user = this.currentConfiguration.created_by?.login
      if (!user) return date
      return `${date} por ${user}`
    },
    title() {
      if (!this.hotsiteSlot) return 'Hotsite'
      return `Hotsite - ${this.hotsiteSlot.name}`
    },
    tabs() {
      return [
        {
          name: 'Cores',
          value: 0,
        },
        {
          name: 'Imagens',
          value: 1,
        },
        {
          name: 'Posicionamento',
          value: 2,
        },
        {
          name: 'Campos',
          value: 3,
        },
        {
          name: 'Avançado',
          value: 4,
        },
      ]
    },
    tabsErrors() {
      const errors = []
      for (let i = 0; i < 5; i++) {
        if (this.getTabValidationErrors(i).length > 0) {
          errors.push(i)
        }
      }
      return errors
    },
    previewUrl() {
      if (!this.hotsiteSlot) return ''
      return `https://preview.querovantagens.com?slot=${this.hotsiteSlot._id}`
    },
  },
  async created() {
    if (this.$route.params.id) {
      await this.fetchHotsiteSlot(this.$route.params.id)
    }
  },
  async beforeRouteLeave(to, from, next) {
    const confirmed = await this.$confirm({
      title: 'Alterações não salvas',
      message: 'Você pode ter alterações não salvas. Deseja descartar?',
      confirmText: 'Sim',
      cancelText: 'Cancelar',
      color: 'warning',
    })
      .then(() => true)
      .catch(() => false)

    if (confirmed) {
      this.clearConfiguration()
      next()
    }
  },
  methods: {
    ...mapActions('hotsiteConfiguration', [
      'fetchHotsiteSlot',
      'clearConfiguration',
      'setCurrentConfiguration',
      'validateConfiguration',
      'clearValidationErrors',
      'setConfigurationProperties',
    ]),
    selectConfiguration(configuration) {
      this.setConfigurationProperties(configuration)
    },
    async saveProduction() {
      if (!this.hotsiteSlot) return

      if (!(await this.validateBeforeSave())) return

      this.savingProduction = true

      try {
        await this.$apollo.mutate({
          mutation: SAVE_HOTSITE_SLOT_PRODUCTION,
          variables: {
            hotsiteSlotId: this.hotsiteSlot._id,
            settings: this.currentConfigurationToSave.settings,
            overrides: this.currentConfigurationToSave.overrides,
          },
        })

        await this.fetchHotsiteSlot(this.hotsiteSlot._id)
        this.setCurrentConfiguration('production')
        this.clearValidationErrors()
      } catch (error) {
        console.error(error)
        this.$snackbar({
          message: 'Erro ao salvar hotsite em produção',
          snackbarColor: 'error',
        })
      } finally {
        this.savingProduction = false
      }
    },
    async savePreview() {
      if (!this.hotsiteSlot) return

      if (!(await this.validateBeforeSave())) return

      this.savingPreview = true

      try {
        await this.$apollo.mutate({
          mutation: SAVE_HOTSITE_SLOT_PREVIEW,
          variables: {
            hotsiteSlotId: this.hotsiteSlot._id,
            settings: this.currentConfigurationToSave.settings,
            overrides: this.currentConfigurationToSave.overrides,
          },
        })

        await this.fetchHotsiteSlot(this.hotsiteSlot._id)
        this.setCurrentConfiguration('preview')
        this.clearValidationErrors()
      } catch (error) {
        console.error(error)
        this.$snackbar({
          message: 'Erro ao salvar hotsite em preview',
          snackbarColor: 'error',
        })
      } finally {
        this.savingPreview = false
      }
    },
    async validateBeforeSave() {
      await this.validateConfiguration()
      if (this.hasValidationErrors) {
        this.$snackbar({
          message: 'Existem campos obrigatórios não preenchidos. Verifique as abas com erros.',
          snackbarColor: 'error',
        })

        // Scroll to top of the page
        window.scrollTo({ top: 0, behavior: 'smooth' })

        return false
      }
      return true
    },
    async discardChanges() {
      const confirmed = await this.$confirm({
        title: 'Descartar alterações',
        message: 'Tem certeza que deseja descartar todas as alterações não salvas?',
        confirmText: 'Sim, descartar',
        cancelText: 'Cancelar',
        color: 'warning',
      })

      if (confirmed) {
        this.clearConfiguration()
        this.$router.push({ name: 'site-cadastramento' })
      }
    },
    copyUrl(url) {
      navigator.clipboard.writeText(url)
    },
    async saveProductionUrls(urls) {
      try {
        await this.$apollo.mutate({
          mutation: SAVE_HOTSITE_PRODUCTION_URLS,
          variables: {
            hotsiteSlotId: this.hotsiteSlot._id,
            urls,
          },
        })
        this.$snackbar({ message: 'URLs salvas com sucesso.', snackbarColor: 'success' })
      } catch (e) {
        console.error(e)
        this.$snackbar({ message: 'Falha ao salvar URLs.', snackbarColor: 'error' })
      }
    },
  },
}
</script>
